import React, { useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useContext } from "react";
import { HGContext } from "../../../../helpers/globals";
import { selectedCong } from "../../../../helpers/langGroups";
import { ISODateString } from "../../../../types/date";
import { Absence } from "../../../../types/scheduling/absence";
import { Events } from "../../../../types/scheduling/events";
import { AssignmentNotification, UserPrivilegeMap } from "../../../../types/scheduling/meetings";
import { CongSettings } from "../../../../types/scheduling/settings";
import {
  Congregation,
  PublicTalkAssignment,
  Speaker,
  WMCoreParts,
  WMSchedule,
} from "../../../../types/scheduling/weekend";
import User from "../../../../types/user";
import { AssignmentSummary } from "../../RecentAssignments";
import { EventBadge } from "../../eventAlert";
import { WeekHeader, PartAssignmentDropdown, SongNumberDropdown } from "./index";
import { wmDate } from "../../../../helpers/dateHelpers";
import { IncomingSection } from "./IncomingSection";
import { OutgoingSection } from "./OutgoingSection";
import { DeleteConfirmModal } from "./modals/DeleteConfirmModal";
import { OutgoingMoveModal } from "./modals/OutgoingMoveModal";
import { SpeakerInfoModal } from "./modals/SpeakerInfoModal";

interface WeekScheduleProps {
  schedule: WMSchedule;
  langGroupId: number;
  refresh: () => void;
  userPrivilegeMap: UserPrivilegeMap;
  userMap: Map<number, User>;
  notificationDates: { start: ISODateString; end: ISODateString };
  crhNotifications?: AssignmentNotification[];
  ptInNotifications?: AssignmentNotification[];
  ptOutNotifications?: AssignmentNotification[];
  scheduleDates: { from: ISODateString; to: ISODateString };
  assignmentMap: Map<number, AssignmentSummary[]>;
  absences?: Absence[];
  congSettings: CongSettings;
  fsGroups?: any[];
  // New props for OutgoingSection
  speakers?: Speaker[];
  congregations?: Congregation[];
  pwSchedules?: any[];
}

export const WeekSchedule: React.FC<WeekScheduleProps> = ({
  schedule,
  langGroupId,
  refresh,
  userPrivilegeMap,
  userMap,
  notificationDates,
  crhNotifications,
  ptInNotifications,
  ptOutNotifications,
  scheduleDates,
  assignmentMap,
  absences,
  congSettings,
  fsGroups,
  speakers = [],
  congregations = [],
  pwSchedules = [],
}) => {
  const [editMode, setEditMode] = useState("");
  // Only using setters for these state variables, prefix with _ to avoid lint errors
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [selectedID, setSelectedID] = useState(0);
  const [out, setOut] = useState({} as PublicTalkAssignment);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSpeakerInfoModal, setShowSpeakerInfoModal] = useState(false);
  const [selectedSpeaker, setSelectedSpeaker] = useState({} as Speaker);
  const { t } = useTranslation();
  const ctx = useContext(HGContext);
  const myCong = selectedCong(langGroupId) ?? ctx.globals.cong!;

  // Get a set of all local cong ids - host cong and all language groups
  const localCongIds = new Set<number>([ctx.globals.cong, ...ctx.globals.language_groups].map((c) => c!.id));

  const meetingDate = wmDate(schedule.date, myCong, schedule.event ? [schedule.event] : undefined);

  // Check if this is a CO event
  const chairmanOnly = schedule.event?.event === Events.co;

  // Check if there are no meetings
  const noMeetingEvents = new Set([Events.ca, Events.rc, Events.wmem]);
  const noMeeting =
    schedule.event && noMeetingEvents.has(schedule.event.event) && ctx.globals.language_groups.length === 0;

  // Handler for speaker info click
  const handleSpeakerInfoClick = (speaker: Speaker) => {
    setSelectedSpeaker(speaker);
    setShowSpeakerInfoModal(true);
  };

  return (
    <>
      <DeleteConfirmModal
        show={showDeleteConfirm}
        setShow={setShowDeleteConfirm}
        id={selectedID}
        refresh={refresh}
        langGroupId={langGroupId}
      />
      <OutgoingMoveModal
        show={showMoveModal}
        setShow={setShowMoveModal}
        out={out}
        refresh={refresh}
        langGroupId={langGroupId}
      />
      <SpeakerInfoModal speaker={selectedSpeaker} show={showSpeakerInfoModal} setShow={setShowSpeakerInfoModal} />
      <Card key={schedule.date}>
        <Card.Body className="background-body">
          <div className="d-flex flex-column flex-xl-row gap-4">
            <div className="flex-shrink-0 align-self-start w-auto">
              <WeekHeader meetingDate={meetingDate} />
            </div>
            <div className="d-flex flex-wrap align-self-start flex-grow-1">
              <EventBadge context="wm" event={schedule.event} />

              {!noMeeting && (
                <>
                  <Row className="col-12">
                    {/* Incoming and Outgoing sections */}
                    <div className="row px-0 mx-0 pt-3 pb-3">
                      <IncomingSection
                        schedule={schedule}
                        langGroupId={langGroupId}
                        editMode={editMode}
                        setEditMode={setEditMode}
                        notifications={ptInNotifications}
                        notificationDates={notificationDates}
                        absences={absences}
                        events={schedule.event ? [schedule.event] : undefined}
                        congSettings={congSettings}
                        userAssignmentMap={assignmentMap}
                        localCongIds={localCongIds}
                        onSpeakerInfoClick={handleSpeakerInfoClick}
                        refresh={refresh}
                      />

                      <OutgoingSection
                        week={schedule}
                        langGroupId={langGroupId}
                        setOut={setOut}
                        setShowMoveModal={setShowMoveModal}
                        speakers={speakers}
                        congregations={congregations}
                        setSelectedID={setSelectedID}
                        setShowDeleteConfirm={setShowDeleteConfirm}
                        notifications={ptOutNotifications}
                        notificationDates={notificationDates}
                        congSettings={congSettings}
                        userAssignmentMap={assignmentMap}
                        pwSchedules={pwSchedules}
                        absences={absences}
                        refetchSchedules={refresh}
                      />
                    </div>
                    <Card className="p-3">
                      <Row className="justify-content-between">
                        <Col xs={12} md={12} lg={4} className="pb-3 pb-lg-0 d-flex align-items-start">
                          <div>
                            <PartAssignmentDropdown
                              part={WMCoreParts.Chairman}
                              schedule={schedule}
                              langGroupId={langGroupId}
                              userPrivilegeMap={userPrivilegeMap}
                              userMap={userMap}
                              refresh={refresh}
                              notificationDates={notificationDates}
                              notifications={crhNotifications}
                              _scheduleDates={scheduleDates}
                              assignmentMap={assignmentMap}
                              meetingDate={meetingDate}
                              absences={absences}
                              congSettings={congSettings}
                              fsGroups={fsGroups}
                            />
                          </div>

                          {schedule.talk_mod !== "tbd" && (
                            <div className="ms-2">
                              <span>
                                <p className="fw-bold mb-1 text-muted">{t("schedules.song %d", { "0": "" })}</p>
                                <SongNumberDropdown schedule={schedule} langGroupId={langGroupId} />
                              </span>
                            </div>
                          )}
                        </Col>
                        <Col xs={12} md={12} lg={4} className="pb-3 pb-lg-0">
                          <PartAssignmentDropdown
                            part={WMCoreParts.OpeningPrayer}
                            schedule={schedule}
                            langGroupId={langGroupId}
                            userPrivilegeMap={userPrivilegeMap}
                            userMap={userMap}
                            refresh={refresh}
                            notificationDates={notificationDates}
                            notifications={crhNotifications}
                            _scheduleDates={scheduleDates}
                            assignmentMap={assignmentMap}
                            meetingDate={meetingDate}
                            absences={absences}
                            congSettings={congSettings}
                            noAutoFill
                            fsGroups={fsGroups}
                          />
                        </Col>
                        {congSettings.show_interpreter && (
                          <Col xs={12} md={12} lg={4} className="pb-3 pb-lg-0">
                            <PartAssignmentDropdown
                              part={WMCoreParts.Interpreter}
                              schedule={schedule}
                              langGroupId={langGroupId}
                              userPrivilegeMap={userPrivilegeMap}
                              userMap={userMap}
                              refresh={refresh}
                              notificationDates={notificationDates}
                              notifications={crhNotifications}
                              _scheduleDates={scheduleDates}
                              assignmentMap={assignmentMap}
                              meetingDate={meetingDate}
                              absences={absences}
                              congSettings={congSettings}
                              noAutoFill
                              fsGroups={fsGroups}
                            />
                          </Col>
                        )}
                      </Row>
                      <Row className="pt-3">
                        <Col xs={12} md={12} lg={4} className="pb-3 pb-lg-0">
                          <PartAssignmentDropdown
                            part={WMCoreParts.WTConductor}
                            schedule={schedule}
                            langGroupId={langGroupId}
                            userPrivilegeMap={userPrivilegeMap}
                            userMap={userMap}
                            refresh={refresh}
                            notificationDates={notificationDates}
                            notifications={crhNotifications}
                            _scheduleDates={scheduleDates}
                            assignmentMap={assignmentMap}
                            meetingDate={meetingDate}
                            absences={absences}
                            congSettings={congSettings}
                            noAutoFill
                            fsGroups={fsGroups}
                          />
                        </Col>
                        {(!chairmanOnly || !!schedule.wm_reader) && (
                          <Col xs={12} md={12} lg={4} className="pb-3 pb-lg-0">
                            <PartAssignmentDropdown
                              disabled={!congSettings.show_wt_reader}
                              part={WMCoreParts.Reader}
                              schedule={schedule}
                              langGroupId={langGroupId}
                              userPrivilegeMap={userPrivilegeMap}
                              userMap={userMap}
                              refresh={refresh}
                              notificationDates={notificationDates}
                              notifications={crhNotifications}
                              _scheduleDates={scheduleDates}
                              assignmentMap={assignmentMap}
                              meetingDate={meetingDate}
                              absences={absences}
                              congSettings={congSettings}
                              fsGroups={fsGroups}
                            />
                          </Col>
                        )}
                        <Col xs={12} md={12} lg={4} className="pb-3 pb-lg-0">
                          <PartAssignmentDropdown
                            part={WMCoreParts.ClosingPrayer}
                            schedule={schedule}
                            langGroupId={langGroupId}
                            userPrivilegeMap={userPrivilegeMap}
                            userMap={userMap}
                            refresh={refresh}
                            notificationDates={notificationDates}
                            notifications={crhNotifications}
                            _scheduleDates={scheduleDates}
                            assignmentMap={assignmentMap}
                            meetingDate={meetingDate}
                            absences={absences}
                            congSettings={congSettings}
                            noAutoFill
                            fsGroups={fsGroups}
                          />
                        </Col>
                      </Row>
                      {((!chairmanOnly && congSettings.enable_hospitality) || !!schedule.host) && (
                        <Row className="pt-3">
                          <Col xs={12} md={12} lg={4} className="pb-3 pb-lg-0">
                            <PartAssignmentDropdown
                              part={WMCoreParts.Host}
                              schedule={schedule}
                              langGroupId={langGroupId}
                              userPrivilegeMap={userPrivilegeMap}
                              userMap={userMap}
                              refresh={refresh}
                              notificationDates={notificationDates}
                              notifications={crhNotifications}
                              _scheduleDates={scheduleDates}
                              assignmentMap={assignmentMap}
                              meetingDate={meetingDate}
                              absences={absences}
                              congSettings={congSettings}
                              fsGroups={fsGroups}
                            />
                          </Col>
                        </Row>
                      )}
                    </Card>
                  </Row>
                </>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};
